import { all, takeEvery, takeLatest } from 'redux-saga/effects';
import { listAchievementsRequest, updateAchievementsInfo, updateAchievementsNotification } from './achievements/sagas';
import { AchievementsTypes } from './achievements/types';
import { getAppOrg, getOrganizationStatsRequest, getPasswordRulesRequest, setConfig } from './appOrganization/sagas';
import { AppOrganizationTypes } from './appOrganization/types';
import { getAttachmentListRequest, postAttachmentRequest, putAttachment, removeAttachmentRequest } from './attachments/sagas';
import { AttachmentsTypes } from './attachments/types';
import {
    getBotRecommendationRequest,
    getChatCategoriesRequest,
    selectRecommendationContentRequest,
    sendChatMessage,
    sendNewEventRequest
} from './bot/saga';
import { ChatActionTypes } from './bot/types';
import { getCategories, getCategory } from './category/sagas';
import { CategoryTypes } from './category/types';
import { activateCategory, createCategory, inactivateCategory } from './categoryManagement/sagas';
import { CategoryManagementTypes } from './categoryManagement/types';
import { getClassificationsRequest } from './classification/saga';
import { ClassificationsTypes } from './classification/types';
import {
    deleteQuestion,
    getQuestionRequest,
    getQuestionsRequest,
    postQuestion,
    postQuestionRenew,
    putQuestion,
    putQuestionReorderRequest
} from './evaluationQuestion/sagas';
import { EvaluationQuestionTypes } from './evaluationQuestion/types';
import {
    batchFavorite,
    getFavoriteListRequest,
    removeFavoriteCategoryRequest,
    removeFavoriteContentRequest,
    removeFavoriteCourseRequest,
    removeFavoriteProgramRequest,
    setFavoriteCategoryRequest,
    setFavoriteContentRequest,
    setFavoriteCourseRequest,
    setFavoriteProgramRequest
} from './favorite/sagas';
import { FavoriteTypes } from './favorite/types';
import { deleteFollowerRequest, getFollowersRequest, getFollowingRequest, setFollowerRequest, updateFollowBatchRequest } from './followers/sagas';
import { FollowerTypes } from './followers/types';
import { highlightsRequest, sendHighlightRequest } from './highlights/sagas';
import { HighlightTypes } from './highlights/types';
import { acceptTermsRequest, auth, authSAML, changePasswordRequest, forgotPasswordRequest, integrationAuth } from './identity/sagas';
import { IdentityTypes } from './identity/types';
import {
    archiveItem,
    deleteItem,
    finishContentRequest,
    getContentListRequest,
    getContentRequest,
    getItem,
    getItens,
    postItem,
    publishItem,
    putItem,
    setContentLinkMetadataRequest,
    startContentRequest,
    toggleItemVisibility,
    updateContentStudentViewRequest
} from './item/sagas';
import { ItemTypes } from './item/types';
import {
    getCurrentLearningMapRequest,
    getHistoryLearningMapRequest,
    getWordCloudCategoriesDataRequest,
    saveCurrentLearningMapRequest
} from './learningMap/saga';
import { LearningMapTypes } from './learningMap/types';
import {
    deleteLesson,
    finishLesson,
    getLesson,
    getLessons,
    getLMSLessons,
    postEvaluation,
    postLesson,
    putEvaluationLesson,
    putLesson,
    reorderLesson,
    setLessonLinkMetadataRequest,
    startLesson
} from './lesson/sagas';
import { LessonTypes } from './lesson/types';
import {
    addContentToCourseRequest,
    archiveList,
    coursesRequest,
    createCourseRequest,
    deleteList,
    finishLessonRequest,
    getCourseRequest,
    getCourseStudentViewRequest,
    getList,
    getLists,
    getScormPackageStatusRequest,
    increaseCourseViewsRequest,
    postList,
    publishList,
    putList,
    startEnrollmentRequest,
    startLessonRequest,
    toggleListVisibility,
    updateCourseRequest,
    updateCourseStudentViewRequest,
    updateScormPackageRequest
} from './list/sagas';
import { ListTypes } from './list/types';
import { addItemsToList, deleteItemFromList, getListItems } from './listItems/sagas';
import { ListItemsTypes } from './listItems/types';
import { getContentLocaleRequest, setContentLocaleRequest, setLocaleRequest } from './locale/saga';
import { LocaleTypes } from './locale/types';
import { completeOnboardRequest } from './onboard/saga';
import { OnboardTypes } from './onboard/types';
import {
    activeUser,
    firstAccess,
    forgotPassword,
    getImportDetail,
    getImportHistory,
    getPeople,
    getPerson,
    getPublicPersonRequest,
    getTotalActivePeople,
    postPerson,
    putPerson,
    updateMyPerson
} from './person/sagas';
import { PeopleTypes } from './person/types';
import {
    addCourseInProgram,
    createProgram,
    getProgramClassStudentView,
    getProgramCourses,
    getProgramListStudentView,
    getProgramPrerequisites,
    getProgramRequest,
    getProgramSearchedRequest,
    getProgramsRequest,
    increaseProgramViewsRequest,
    publishProgram,
    removeCourseFromProgram,
    reorderProgramCourse,
    setProgramPrerequisitesRequest,
    setProgramReady,
    updateProgram,
    updateProgramClassStudentView
} from './program/saga';
import { ProgramTypes } from './program/types';
import {
    createProgramClassForCourseRequest,
    createProgramClassForProgramRequest,
    deleteProgramClass,
    enroll,
    getEnrolledStudents,
    getEnrollmentByStudent,
    getProgramClass,
    getStudentsWithEnrollInfo,
    massImportEnrollment,
    unenroll,
    updateProgramClass
} from './programClass/sagas';
import { ProgramClassTypes } from './programClass/types';
import { getPushNotificationListRequest, updatePushNotificationStatusRequest } from './pushNotification/sagas';
import { PushNotificationTypes } from './pushNotification/types';
import {
    finishQuestionnaire,
    generateQuestionnaire,
    getQuestion,
    getQuestionnaire,
    getQuestionnaireResponseSummary,
    listQuestionnairesRequest,
    startQuestionnaire,
    updateAnswer
} from './questionnaire/sagas';
import { QuestionnaireTypes } from './questionnaire/types';
import {
    getAutoExecutableQuizRequest,
    getQuizQuestionListRequest,
    getQuizResultListRequest,
    getQuizResultRequest,
    postQuizQuestionRequest,
    postQuizRequest,
    postQuizResultRequest,
    putQuizQuestionReorderRequest
} from './quiz/saga';
import { QuizTypes } from './quiz/types';
import { getRateRequest, rateStudentContentRequest, rateStudentCourseRequest, rateStudentProgramRequest } from './rating/sagas';
import { RatingTypes } from './rating/types';
import { downloadLastReport, generateAsyncReportRequest, listReportProcessesRequest } from './report/saga';
import { ReportTypes } from './report/types';
import { getUrlRedirectSaml } from './saml/sagas';
import { SamlTypes } from './saml/types';
import { startCDNTest } from './scorm/sagas';
import { ScormTypes } from './scorm/types';
import { deleteSearchHistoryRequest, getSearchHistoryRequest, getSearchSuggestionsRequest } from './search/saga';
import { SearchTypes } from './search/types';
import {
    addContentToCourseRequest as addContentToStudentCourseRequest,
    deleteContentFromCourseRequest,
    getAddedContentList,
    reorderContentListRequest
} from './student/sagas';
import { StudentTypes } from './student/types';
import { getTags } from './tag/sagas';
import { TagTypes } from './tag/types';
import { getUsers, postFeedback } from './user/sagas';
import { UserTypes } from './user/types';

export default function* rootSaga() {
    yield all([
        takeLatest(IdentityTypes.AUTH_REQUEST, auth),
        takeLatest(IdentityTypes.ACCEPT_TERMS_REQUEST, acceptTermsRequest),
        takeLatest(IdentityTypes.FORGOT_PASSWORD_REQUEST, forgotPasswordRequest),
        takeLatest(IdentityTypes.CHANGE_PASSWORD_REQUEST, changePasswordRequest),
        takeLatest(AppOrganizationTypes.GET_APP_ORGANIZATION_REQUEST, getAppOrg),
        takeLatest(AppOrganizationTypes.SET_CONFIG_REQUEST, setConfig),
        takeLatest(ItemTypes.GET_ITEM_REQUEST, getItem),
        takeLatest(ItemTypes.GET_ITEMS_REQUEST, getItens),
        takeLatest(ItemTypes.POST_ITEM_REQUEST, postItem),
        takeLatest(ItemTypes.PUT_ITEM_REQUEST, putItem),
        takeLatest(ItemTypes.DELETE_ITEM_REQUEST, deleteItem),
        takeLatest(ItemTypes.TOGGLE_ITEM_VISIBILITY_REQUEST, toggleItemVisibility),
        takeLatest(ItemTypes.PUBLISH_ITEM_REQUEST, publishItem),
        takeLatest(ItemTypes.ARCHIVE_ITEM_REQUEST, archiveItem),
        takeLatest(TagTypes.GET_TAGS_REQUEST, getTags),
        takeLatest(CategoryTypes.GET_CATEGORY_REQUEST, getCategory),
        takeLatest(CategoryTypes.GET_CATEGORIES_REQUEST, getCategories),
        takeLatest(ListTypes.GET_LIST_REQUEST, getList),
        takeLatest(ListTypes.GET_LISTS_REQUEST, getLists),
        takeLatest(ListTypes.POST_LIST_REQUEST, postList),
        takeLatest(ListTypes.PUT_LIST_REQUEST, putList),
        takeLatest(ListTypes.DELETE_LIST_REQUEST, deleteList),
        takeLatest(ListTypes.TOGGLE_LIST_VISIBILITY_REQUEST, toggleListVisibility),
        takeLatest(ListTypes.PUBLISH_LIST_REQUEST, publishList),
        takeLatest(ListTypes.ARCHIVE_LIST_REQUEST, archiveList),
        takeLatest(ListItemsTypes.ADD_TO_LIST_REQUEST, addItemsToList),
        takeLatest(ListItemsTypes.DELETE_FROM_LIST_REQUEST, deleteItemFromList),
        takeLatest(ListItemsTypes.GET_LIST_ITEMS_REQUEST, getListItems),
        takeEvery(ListTypes.COURSES_REQUEST, coursesRequest),
        takeLatest(ListTypes.START_LESSON_REQUEST, startLessonRequest),
        takeEvery(ListTypes.FINISH_LESSON_REQUEST, finishLessonRequest),
        takeLatest(UserTypes.GET_USERS_REQUEST, getUsers),
        takeLatest(UserTypes.POST_FEEDBACK_REQUEST, postFeedback),
        takeLatest(PeopleTypes.GET_PERSON_REQUEST, getPerson),
        takeLatest(PeopleTypes.GET_PEOPLE_REQUEST, getPeople),
        takeLatest(PeopleTypes.ACTIVE_REQUEST, activeUser),
        takeLatest(PeopleTypes.FIRST_ACCESS_REQUEST, firstAccess),
        takeLatest(PeopleTypes.FORGOT_PASSWORD_REQUEST, forgotPassword),
        takeLatest(PeopleTypes.POST_PERSON_REQUEST, postPerson),
        takeLatest(PeopleTypes.PUT_PERSON_REQUEST, putPerson),
        takeLatest(PeopleTypes.GET_IMPORT_HISTORY_REQUEST, getImportHistory),
        takeLatest(PeopleTypes.GET_IMPORT_DETAIL_REQUEST, getImportDetail),
        takeEvery(ItemTypes.GET_CONTENT_LIST_REQUEST, getContentListRequest),
        takeLatest(FavoriteTypes.SET_COURSE_FAVORITE_REQUEST, setFavoriteCourseRequest),
        takeLatest(FavoriteTypes.REMOVE_COURSE_FAVORITE_REQUEST, removeFavoriteCourseRequest),
        takeLatest(FavoriteTypes.SET_CONTENT_FAVORITE_REQUEST, setFavoriteContentRequest),
        takeLatest(FavoriteTypes.REMOVE_CONTENT_FAVORITE_REQUEST, removeFavoriteContentRequest),
        takeLatest(FavoriteTypes.SET_PROGRAM_FAVORITE_REQUEST, setFavoriteProgramRequest),
        takeLatest(FavoriteTypes.REMOVE_PROGRAM_FAVORITE_REQUEST, removeFavoriteProgramRequest),
        takeLatest(FavoriteTypes.GET_FAVORITE_LIST_REQUEST, getFavoriteListRequest),
        takeLatest(ItemTypes.GET_CONTENT_REQUEST, getContentRequest),
        takeLatest(AttachmentsTypes.GET_ATTACHMENTS_REQUEST, getAttachmentListRequest),
        takeLatest(AttachmentsTypes.REMOVE_ATTACHMENT_REQUEST, removeAttachmentRequest),
        takeLatest(AttachmentsTypes.PUT_ATTACHMENT_REQUEST, putAttachment),
        takeLatest(HighlightTypes.HIGHLIGHTS_REQUEST, highlightsRequest),
        takeLatest(HighlightTypes.SEND_HIGHLIGHT_REQUEST, sendHighlightRequest),
        takeLatest(ItemTypes.START_CONTENT_REQUEST, startContentRequest),
        takeLatest(ItemTypes.FINISH_CONTENT_REQUEST, finishContentRequest),
        takeLatest(ClassificationsTypes.GET_CLASSIFICATIONS_REQUEST, getClassificationsRequest),
        takeLatest(RatingTypes.SET_CONTENT_RATE_REQUEST, rateStudentContentRequest),
        takeLatest(ItemTypes.UPDATE_CONTENT_STUDENT_VIEW_REQUEST, updateContentStudentViewRequest),
        takeLatest(ListTypes.GET_COURSE_STUDENT_VIEW_REQUEST, getCourseStudentViewRequest),
        takeLatest(ListTypes.UPDATE_COURSE_STUDENT_VIEW_REQUEST, updateCourseStudentViewRequest),
        takeLatest(RatingTypes.SET_COURSE_RATE_REQUEST, rateStudentCourseRequest),
        takeLatest(ItemTypes.SET_CONTENT_LINK_METADATA_REQUEST, setContentLinkMetadataRequest),
        takeEvery(PeopleTypes.GET_TOTAL_ACTIVE_PEOPLE_REQUEST, getTotalActivePeople),
        takeLatest(SearchTypes.GET_SEARCH_HISTORY_REQUEST, getSearchHistoryRequest),
        takeLatest(SearchTypes.DELETE_SEARCH_HISTORY_REQUEST, deleteSearchHistoryRequest),
        takeLatest(ListTypes.SEARCHED_SUCCESS, getSearchHistoryRequest),
        takeLatest(ItemTypes.SEARCHED_SUCCESS, getSearchHistoryRequest),
        takeLatest(SearchTypes.GET_SEARCH_SUGGESTIONS_REQUEST, getSearchSuggestionsRequest),
        takeLatest(LocaleTypes.SET_LOCALE_REQUEST, setLocaleRequest),
        takeLatest(AppOrganizationTypes.GET_PASSWORD_RULES_REQUEST, getPasswordRulesRequest),
        takeEvery(PushNotificationTypes.GET_PUSH_NOTIFICATION_LIST_REQUEST, getPushNotificationListRequest),
        takeLatest(PushNotificationTypes.UPDATE_PUSH_NOTIFICATION_STATUS, updatePushNotificationStatusRequest),
        takeLatest(ListTypes.CREATE_COURSE_REQUEST, createCourseRequest),
        takeLatest(ListTypes.UPDATE_COURSE_REQUEST, updateCourseRequest),
        takeLatest(ListTypes.ADD_CONTENT_TO_COURSE_REQUEST, addContentToCourseRequest),
        takeLatest(LessonTypes.GET_LESSON_REQUEST, getLesson),
        takeLatest(LessonTypes.GET_LMS_LESSONS_REQUEST, getLMSLessons),
        takeLatest(LessonTypes.GET_LESSONS_REQUEST, getLessons),
        takeLatest(LessonTypes.SET_LESSON_EXTERNAL_LINK_METADATA_REQUEST, setLessonLinkMetadataRequest),
        takeLatest(LessonTypes.POST_LESSON_REQUEST, postLesson),
        takeLatest(LessonTypes.POST_EVALUATION_REQUEST, postEvaluation),
        takeLatest(LessonTypes.PUT_LESSON_REQUEST, putLesson),
        takeLatest(LessonTypes.PUT_LESSON_REORDER_REQUEST, reorderLesson),
        takeLatest(LessonTypes.PUT_EVALUATION_LESSON_REQUEST, putEvaluationLesson),
        takeLatest(LessonTypes.DELETE_LESSON_REQUEST, deleteLesson),
        takeLatest(LessonTypes.START_LESSON_REQUEST, startLesson),
        takeLatest(LessonTypes.FINISH_LESSON_REQUEST, finishLesson),
        takeLatest(ListTypes.GET_COURSE_REQUEST, getCourseRequest),
        takeLatest(ListTypes.UPDATE_SCORM_PACKAGE_REQUEST, updateScormPackageRequest),
        takeLatest(ListTypes.GET_SCORM_PACKAGE_STATUS_REQUEST, getScormPackageStatusRequest),
        takeLatest(ProgramClassTypes.CREATE_PROGRAM_CLASS_FOR_COURSE_REQUEST, createProgramClassForCourseRequest),
        takeLatest(ProgramClassTypes.CREATE_PROGRAM_CLASS_FOR_PROGRAM_REQUEST, createProgramClassForProgramRequest),
        takeLatest(ProgramClassTypes.UPDATE_PROGRAM_CLASS_REQUEST, updateProgramClass),
        takeLatest(ProgramClassTypes.DELETE_PROGRAM_CLASS_REQUEST, deleteProgramClass),
        takeLatest(ProgramClassTypes.GET_PROGRAM_CLASS_REQUEST, getProgramClass),
        takeLatest(ProgramClassTypes.GET_ENROLLED_STUDENTS_REQUEST, getEnrolledStudents),
        takeLatest(ProgramClassTypes.GET_ENROLLMENT_BY_STUDENT_REQUEST, getEnrollmentByStudent),
        takeEvery(ProgramClassTypes.ENROLL_STUDENT_REQUEST, enroll),
        takeEvery(ProgramClassTypes.UNENROLL_STUDENT_REQUEST, unenroll),
        takeLatest(EvaluationQuestionTypes.GET_QUESTION_REQUEST, getQuestionRequest),
        takeLatest(EvaluationQuestionTypes.GET_QUESTIONS_REQUEST, getQuestionsRequest),
        takeLatest(QuestionnaireTypes.LIST_QUESTIONNAIRES_REQUEST, listQuestionnairesRequest),
        takeLatest(QuestionnaireTypes.GENERATE_QUESTIONNAIRE_REQUEST, generateQuestionnaire),
        takeLatest(QuestionnaireTypes.UPDATE_ANSWER_REQUEST, updateAnswer),
        takeLatest(QuestionnaireTypes.GET_QUESTIONNAIRE_REQUEST, getQuestionnaire),
        takeLatest(QuestionnaireTypes.GET_QUESTION_REQUEST, getQuestion),
        takeLatest(QuestionnaireTypes.FINISH_QUESTIONNAIRE_REQUEST, finishQuestionnaire),
        takeLatest(QuestionnaireTypes.GET_QUESTIONNAIRE_RESULT_REQUEST, getQuestionnaireResponseSummary),
        takeLatest(QuestionnaireTypes.START_QUESTIONNAIRE_REQUEST, startQuestionnaire),
        takeLatest(EvaluationQuestionTypes.POST_QUESTION_REQUEST, postQuestion),
        takeLatest(EvaluationQuestionTypes.PUT_QUESTION_REQUEST, putQuestion),
        takeLatest(EvaluationQuestionTypes.PUT_QUESTION_REORDER_REQUEST, putQuestionReorderRequest),
        takeLatest(EvaluationQuestionTypes.DELETE_QUESTION_REQUEST, deleteQuestion),
        takeLatest(EvaluationQuestionTypes.POST_QUESTION_RENEW_REQUEST, postQuestionRenew),
        takeLatest(ScormTypes.START_CDN_TEST_REQUEST, startCDNTest),
        takeLatest(ProgramTypes.CREATE_PROGRAM_REQUEST, createProgram),
        takeLatest(ProgramTypes.UPDATE_PROGRAM_REQUEST, updateProgram),
        takeLatest(OnboardTypes.COMPLETE_ONBOARD_REQUEST, completeOnboardRequest),
        takeLatest(ProgramTypes.GET_PROGRAMS_REQUEST, getProgramsRequest),
        takeLatest(ProgramTypes.GET_PROGRAM_REQUEST, getProgramRequest),
        takeEvery(FavoriteTypes.SET_CATEGORY_FAVORITE_REQUEST, setFavoriteCategoryRequest),
        takeEvery(FavoriteTypes.REMOVE_CATEGORY_FAVORITE_REQUEST, removeFavoriteCategoryRequest),
        takeEvery(ProgramTypes.REORDER_PROGRAM_COURSE_REQUEST, reorderProgramCourse),
        takeLatest(ProgramTypes.GET_PROGRAM_COURSES_LIST_REQUEST, getProgramCourses),
        takeLatest(ProgramTypes.REMOVE_PROGRAM_COURSES_REQUEST, removeCourseFromProgram),
        takeLatest(ProgramTypes.ADD_COURSE_IN_PROGRAM_REQUEST, addCourseInProgram),
        takeLatest(IdentityTypes.AUTH_INTEGRATION_REQUEST, integrationAuth),
        takeLatest(ProgramTypes.PUBLISH_PROGRAM_REQUEST, publishProgram),
        takeEvery(ProgramTypes.GET_PROGRAM_LIST_STUDENT_VIEW_REQUEST, getProgramListStudentView),
        takeEvery(ProgramTypes.GET_PROGRAM_CLASS_STUDENT_VIEW_REQUEST, getProgramClassStudentView),
        takeLatest(ProgramTypes.GET_PROGRAM_PREREQUISITES_REQUEST, getProgramPrerequisites),
        takeLatest(ProgramTypes.SET_PROGRAM_PREREQUISITES_REQUEST, setProgramPrerequisitesRequest),
        takeLatest(ProgramTypes.SET_PROGRAM_READY_REQUEST, setProgramReady),
        takeLatest(QuizTypes.POST_QUIZ_REQUEST, postQuizRequest),
        takeLatest(QuizTypes.GET_AUTO_EXECUTABLE_QUIZ_REQUEST, getAutoExecutableQuizRequest),
        takeLatest(QuizTypes.GET_QUIZ_RESULT_LIST_REQUEST, getQuizResultListRequest),
        takeLatest(QuizTypes.GET_QUIZ_RESULT_REQUEST, getQuizResultRequest),
        takeLatest(QuizTypes.POST_QUIZ_RESULT_REQUEST, postQuizResultRequest),
        takeLatest(QuizTypes.GET_QUIZ_QUESTION_LIST_REQUEST, getQuizQuestionListRequest),
        takeLatest(QuizTypes.POST_QUIZ_QUESTION_REQUEST, postQuizQuestionRequest),
        takeLatest(QuizTypes.PUT_QUIZ_QUESTION_REORDER_REQUEST, putQuizQuestionReorderRequest),
        takeLatest(LearningMapTypes.GET_CURRENT_LEARNING_MAP_REQUEST, getCurrentLearningMapRequest),
        takeLatest(LearningMapTypes.SAVE_CURRENT_LEARNING_MAP_REQUEST, saveCurrentLearningMapRequest),
        takeLatest(FavoriteTypes.BATCH_FAVORITE_REQUEST, batchFavorite),
        takeEvery(ChatActionTypes.COMPLETE_SEND_CHAT_MESSAGE_REQUEST, sendChatMessage),
        takeLatest(ChatActionTypes.GET_CATEGORIES_REQUEST, getChatCategoriesRequest),
        takeLatest(ChatActionTypes.GET_RECOMMENDATION_REQUEST, getBotRecommendationRequest),
        takeLatest(ChatActionTypes.SEND_NEW_EVENT_REQUEST, sendNewEventRequest),
        takeLatest(ChatActionTypes.SELECT_RECOMMENDATION_CONTENT_REQUEST, selectRecommendationContentRequest),
        takeLatest(ReportTypes.GENERATE_ASYNC_REPORT_REQUEST, generateAsyncReportRequest),
        takeEvery(ReportTypes.LIST_REPORT_PROCESSES_REQUEST, listReportProcessesRequest),
        takeLatest(StudentTypes.GET_ADDED_CONTENT_LIST_REQUEST, getAddedContentList),
        takeLatest(StudentTypes.ADD_CONTENT_TO_COURSE_REQUEST, addContentToStudentCourseRequest),
        takeLatest(StudentTypes.DELETE_CONTENT_FROM_COURSE_REQUEST, deleteContentFromCourseRequest),
        takeLatest(StudentTypes.REORDER_CONTENT_LIST_REQUEST, reorderContentListRequest),
        takeLatest(ProgramTypes.SEARCHED_REQUEST, getProgramSearchedRequest),
        takeLatest(AppOrganizationTypes.GET_ORGANIZATION_STATS_REQUEST, getOrganizationStatsRequest),
        takeLatest(LearningMapTypes.GET_HISTORY_LEARNING_MAP_REQUEST, getHistoryLearningMapRequest),
        takeLatest(LearningMapTypes.GET_WORDCLOUD_CATEGORIES_DATA_REQUEST, getWordCloudCategoriesDataRequest),
        takeLatest(AttachmentsTypes.POST_ATTACHMENT_REQUEST, postAttachmentRequest),
        takeLatest(PeopleTypes.REFRESH_MY_PERSON_REQUEST, updateMyPerson),
        takeLatest(RatingTypes.SET_PROGRAM_RATE_REQUEST, rateStudentProgramRequest),
        takeLatest(ProgramTypes.UPDATE_PROGRAM_CLASS_STUDENT_VIEW_REQUEST, updateProgramClassStudentView),
        takeLatest(PeopleTypes.GET_PUBLIC_PERSON_REQUEST, getPublicPersonRequest),
        takeLatest(FollowerTypes.GET_FOLLOWERS_REQUEST, getFollowersRequest),
        takeLatest(FollowerTypes.SET_FOLLOWER_REQUEST, setFollowerRequest),
        takeLatest(FollowerTypes.DELETE_FOLLOWER_REQUEST, deleteFollowerRequest),
        takeLatest(RatingTypes.GET_RATE_REQUEST, getRateRequest),
        takeLatest(ListTypes.INCREASE_COURSE_VIEWS_REQUEST, increaseCourseViewsRequest),
        takeLatest(FollowerTypes.GET_FOLLOWING_REQUEST, getFollowingRequest),
        takeLatest(FollowerTypes.BATCH_FOLLOW_REQUEST, updateFollowBatchRequest),
        takeLatest(ListTypes.START_ENROLLMENT_REQUEST, startEnrollmentRequest),
        takeLatest(AchievementsTypes.UPDATE_ACHIEVEMENTS_NOTIFICATION_REQUEST, updateAchievementsNotification),
        takeLatest(AchievementsTypes.UPDATE_ACHIEVEMENTS_INFO_REQUEST, updateAchievementsInfo),
        takeLatest(AchievementsTypes.LIST_ACHIEVEMENTS_REQUEST, listAchievementsRequest),
        takeLatest(CategoryManagementTypes.CREATE_CATEGORY_REQUEST, createCategory),
        takeLatest(CategoryManagementTypes.ACTIVATE_CATEGORY_RESQUEST, activateCategory),
        takeLatest(CategoryManagementTypes.INACTIVATE_CATEGORY_RESQUEST, inactivateCategory),
        takeLatest(ProgramClassTypes.GET_STUDENTS_WITH_ENROLL_INFO_REQUEST, getStudentsWithEnrollInfo),
        takeLatest(SamlTypes.SAML_REQUEST, getUrlRedirectSaml),
        takeLatest(IdentityTypes.AUTH_SAML_REQUEST, authSAML),
        takeLatest(ProgramClassTypes.MASS_IMPORT_ENROLLMENT_REQUEST, massImportEnrollment),
        takeLatest(ProgramTypes.INCREASE_PROGRAM_VIEWS_REQUEST, increaseProgramViewsRequest),
        takeLatest(LocaleTypes.GET_CONTENT_LOCALE_REQUEST, getContentLocaleRequest),
        takeLatest(LocaleTypes.SET_CONTENT_LOCALE_REQUEST, setContentLocaleRequest),
        takeEvery(ReportTypes.DOWNLOAD_LAST_REPORT_REQUEST, downloadLastReport),
    ]);
}
